import React, { useState } from 'react';
import { ArrowLeft, CheckCircle2, HeartPulse, LogIn, LogOut, Trash2 } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { usePatientDetail } from '../hooks/usePatientDetail';
import { getKnownDepartments } from '../utils/patientViewModel';
import { patientApi } from '../services/patientApi';

const fmt = (value) => (value ? new Date(value).toLocaleString() : '—');

export default function PatientDetail() {
    const { id } = useParams();
  const navigate = useNavigate();
const [deletingPatient, setDeletingPatient] = useState(false);

  const {
    patient,
    eventos,
    loading,
    error,
    enteredAt,
    setEnteredAt,
    motivo,
    setMotivo,
    saving,
    discharging,
    toast,
    nuevoEvento,
    setNuevoEvento,
    department,
    setDepartment,
    deptSaving,
    hasOpenEntry,
    isDischarged,
    sortedUci,
    entrarUci,
    salirUci,
    darDeAlta,
    eliminarIcuEntry,
    eliminarEvento,
    deleting,
    crearEvento,
    guardarDepartamento,
  } = usePatientDetail(id);

  const knownDepartments = getKnownDepartments();


  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <div className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-white" />
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="h-64 animate-pulse rounded-2xl border border-slate-200 bg-white" />
            <div className="h-64 animate-pulse rounded-2xl border border-slate-200 bg-white" />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error || !patient) {
    return (
      <AppLayout>
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
          {error || 'Paciente no encontrado.'}
        </div>
      </AppLayout>
    );
  }

  
  const nombrePaciente = `${patient.firstName || ''}${patient.lastName ? ` ${patient.lastName}` : ''}`.trim();

  const eliminarPaciente = async () => {
  const confirmado = window.confirm(
    `¿Está seguro de eliminar al paciente "${nombrePaciente}"?\n\nEsta acción no se puede deshacer.`
  );

  if (!confirmado) {
    return;
  }

  try {
    setDeletingPatient(true);

    await patientApi.deletePatient(id);

    navigate('/');
  } catch (err) {
    console.error('Error eliminando paciente:', err);
    console.error('Respuesta:', err?.response?.data);

    alert(
      err?.response?.data?.message ||
      'No se pudo eliminar el paciente.'
    );
  } finally {
    setDeletingPatient(false);
  }
};

  return (
    <AppLayout>
      <div className="space-y-6">
        {toast && (
          <div className={`fixed right-4 top-4 z-50 rounded-xl border px-4 py-3 shadow-lg ${toast.type === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-teal-200 bg-white text-slate-700'}`}>
            <div className="flex items-center gap-2">
              {toast.type === 'error' ? <span className="text-sm font-semibold">✕</span> : <CheckCircle2 className="h-4 w-4" />}
              <p className="text-sm font-medium">{toast.message}</p>
            </div>
          </div>
        )}

        <button
          onClick={() => (window.location.href = '/')}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition hover:border-teal-500 hover:text-teal-700"
        >
          <ArrowLeft className="h-4 w-4" /> Volver al panel
        </button>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900">{nombrePaciente}</h1>
              <p className="mt-2 text-sm text-slate-500">
                Exp. {patient.medicalRecordNumber || '—'} · {patient.birthDate ? `${new Date().getFullYear() - new Date(patient.birthDate).getFullYear()} años` : '—'} · {patient.habitacion || '—'}
              </p>
              <p className="mt-4 text-sm leading-6 text-slate-700">{patient.notes || 'Sin diagnóstico registrado'}</p>
            </div>
            <div className={`inline-flex flex-col rounded-2xl border px-4 py-3 text-sm font-medium shadow-sm ${hasOpenEntry ? 'border-red-200 bg-red-50 text-red-700' : isDischarged ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-teal-200 bg-teal-50 text-teal-800'}`}>
              <div className="flex items-center gap-2">
                <HeartPulse className="h-4 w-4" />
                <span>{hasOpenEntry ? 'Actualmente en UCI' : isDischarged ? 'Paciente dado de alta' : 'En planta'}</span>
              </div>
              <span className="mt-1 text-xs opacity-80">{hasOpenEntry ? 'Seguimiento activo en UCI' : isDischarged ? 'Alta médica registrada' : 'Hospitalización en planta'}</span>
            </div>
          </div>

          <dl className="mt-6 grid gap-4 border-t border-slate-100 pt-5 text-sm text-slate-600 sm:grid-cols-3">
            <div>
              <dt className="text-slate-400">Habitación</dt>
              <dd className="mt-1 font-medium text-slate-900">{patient.habitacion || '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-400">Ingreso</dt>
              <dd className="mt-1 font-medium text-slate-900">{fmt(patient.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-slate-400">Estado</dt>
              <dd className="mt-1 font-medium capitalize text-slate-900">{patient.estado || 'hospitalizado'}</dd>
            </div>
          </dl>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="button" onClick={darDeAlta} disabled={discharging || isDischarged} className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-70">
              <CheckCircle2 className="h-4 w-4" /> {discharging ? 'Dando de alta…' : isDischarged ? 'Paciente dado de alta' : 'Dar de alta'}
            </button>
            <select value={department} onChange={(event) => setDepartment(event.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100">
              <option value="">Sin departamento</option>
              {knownDepartments.map((departmentName) => (
                <option key={departmentName} value={departmentName}>{departmentName}</option>
              ))}
              <option value="Otros">Otros</option>
            </select>
            <button type="button" onClick={guardarDepartamento} className="rounded-xl bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-70" disabled={deptSaving}>
              {deptSaving ? 'Guardando…' : 'Guardar departamento'}
            </button>
            <button
                  type="button"
                  onClick={eliminarPaciente}
                  disabled={deletingPatient}
                  className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <Trash2 className="h-4 w-4" />
                  {deletingPatient ? 'Eliminando…' : 'Eliminar paciente'}
                </button>
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-2">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-900">Movimientos de UCI</h2>
              <div className="rounded-full bg-slate-100 p-2 text-slate-600">
                <HeartPulse className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <input
                className="flex-1 min-w-[220px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:bg-slate-100 disabled:text-slate-400"
                placeholder="Motivo (opcional)"
                value={motivo}
                onChange={(event) => setMotivo(event.target.value)}
                disabled={isDischarged}
              />
              {hasOpenEntry ? (
                <button type="button" onClick={salirUci} disabled={saving} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200 disabled:opacity-70">
                  <LogOut className="h-4 w-4" /> {saving ? 'Procesando…' : 'Registrar salida'}
                </button>
              ) : (
                <button type="button" onClick={entrarUci} disabled={saving || isDischarged} className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-teal-700 disabled:opacity-70">
                  <LogIn className="h-4 w-4" /> {saving ? 'Procesando…' : 'Registrar entrada'}
                </button>
              )}
            </div>
            {isDischarged && (
              <p className="mt-3 text-sm text-slate-500">Paciente dado de alta: no se pueden registrar nuevas entradas a UCI.</p>
            )}
            <ul className="mt-5 space-y-3">
              {sortedUci.length === 0 && <li className="rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">Sin ingresos a UCI registrados.</li>}
              {sortedUci.map((entry) => (
                <li key={entry.id} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">Entrada: {fmt(entry.enteredAt || entry.fecha_entrada || entry.createdAt)}</p>
                    <button
                      type="button"
                      onClick={() => eliminarIcuEntry(entry.id)}
                      disabled={deleting}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Eliminar
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-500">{entry.reason || entry.motivo_entrada || 'Sin motivo'}</span>
                    <p className="text-sm text-slate-600">Salida: {fmt(entry.leftAt || entry.fecha_salida || entry.updatedAt)}</p>
                  </div>
                  {(entry.reason || entry.motivo_entrada || entry.motivo_salida) && (
                    <p className="mt-2 text-sm text-slate-500">{[entry.reason, entry.motivo_entrada, entry.motivo_salida].filter(Boolean).join(' → ')}</p>
                  )}
                </li>
              ))}
             </ul>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-900">Novedades y eventos</h2>
              <div className="rounded-full bg-teal-50 p-2 text-teal-700">
                <HeartPulse className="h-4 w-4" />
              </div>
            </div>
            <form onSubmit={crearEvento} className="mt-4 flex flex-wrap gap-2">
              <select
                className="min-w-[140px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:bg-slate-100 disabled:text-slate-400"
                value={nuevoEvento.tipo}
                onChange={(event) => setNuevoEvento((current) => ({ ...current, tipo: event.target.value }))}
                disabled={isDischarged}
              >
                <option value="estado">Estado</option>
                <option value="medicamento">Medicamento</option>
                <option value="procedimiento">Procedimiento</option>
                <option value="laboratorio">Laboratorio</option>
                <option value="otro">Otro</option>
              </select>
              <input
                className="flex-1 min-w-[220px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:bg-slate-100 disabled:text-slate-400"
                placeholder="Descripción de la novedad"
                value={nuevoEvento.descripcion}
                onChange={(event) => setNuevoEvento((current) => ({ ...current, descripcion: event.target.value }))}
                disabled={isDischarged}
              />
              <button type="submit" disabled={isDischarged} className="rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-70">
                Añadir
              </button>
            </form>
            {isDischarged && (
              <p className="mt-3 text-sm text-slate-500">Paciente dado de alta: no se pueden registrar novedades.</p>
            )}
            <ul className="mt-5 space-y-3">
              {eventos.length === 0 && <li className="rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">Todavía no hay novedades registradas.</li>}
              {eventos.map((event) => (
                <li key={event.id} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">{event.tipo} · {fmt(event.fecha)}</p>
                      <p className="mt-2 text-sm text-slate-700">{event.descripcion}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => eliminarEvento(event.id)}
                      disabled={deleting}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Eliminar
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </AppLayout>
  );
}
